import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useHubMember } from "@/lib/HubMemberContext";
import { toast } from "sonner";
import { startCheckout } from "@/lib/checkout";
import {
  Check, Clock, Infinity as InfinityIcon, Zap, ChevronRight,
  ShieldCheck, ArrowRight, Trophy, Sparkles,
} from "lucide-react";

const TYPE_META = {
  hours:     { icon: Clock, label: "hours",    unit: "hrs" },
  overs:     { icon: Zap,   label: "overs",    unit: "overs" },
  sessions:  { icon: Trophy, label: "sessions", unit: "sessions" },
  unlimited: { icon: InfinityIcon, label: "unlimited", unit: "unlimited" },
};

export default function Membership() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const { user } = useAuth();
  const { member: hubMember } = useHubMember();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get("/public/membership-plans")
      .then((r) => setPlans(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const facilities = Array.from(new Set(plans.map((p) => p.facility_slug)));
  const shown = filter === "all" ? plans : plans.filter((p) => p.facility_slug === filter);

  const handleBuy = async (plan) => {
    if (!user) { navigate("/member/login?next=/membership"); return; }
    if (user.role !== "member") {
      toast.error("Please log in with a member account to purchase a plan.");
      return;
    }
    try {
      const { data } = await api.post("/member/purchase", { plan_id: plan.id });
      if (data.razorpay) {
        // Live checkout — provider chosen by admin (Razorpay or PhonePe)
        await startCheckout({
          kind: "membership",
          ref_id: data.plan_id,
          member_id: data.member_id,
          return_to: "/member",
          onSuccess: () => {
            toast.success(`${plan.name} activated!`);
            navigate("/member");
          },
        });
      } else {
        // Fallback (no gateway configured): membership was created inline
        toast.success(`${plan.name} activated!`);
        navigate("/member");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    }
  };

  return (
    <div data-testid="membership-catalog">
      {/* HERO */}
      <section className="relative border-b border-white/8 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=2000" alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-[#0B1120]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="font-mono text-[11px] uppercase tracking-[0.4em] brand-orange mb-3">
            // Facility Memberships · Play More · Save More
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none" data-testid="membership-hero-title">
            Become a Member
          </h1>
          <p className="mt-4 max-w-2xl text-white/70 text-sm md:text-lg">
            Buy hours, overs, sessions or unlimited access to the facilities you love — book faster, save more, and skip the queue.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {[
              { icon: ShieldCheck, t: "Members-only pricing" },
              { icon: Sparkles, t: "Priority booking window" },
              { icon: Clock, t: "Reserve balance any time" },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 border border-white/12 bg-black/40 text-[11px] font-mono uppercase tracking-widest">
                <b.icon size={12} className="brand-blue" /> {b.t}
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            {hubMember ? (
              <Link to="/me" data-testid="mem-hero-portal"
                className="bg-brand-blue text-white px-5 py-3 font-bold text-xs uppercase tracking-widest hover:glow-blue inline-flex items-center gap-2">
                My Portal <ArrowRight size={14} />
              </Link>
            ) : (
              <Link to="/member/login" data-testid="mem-hero-signin"
                className="bg-brand-orange text-black px-5 py-3 font-bold text-xs uppercase tracking-widest hover:bg-white inline-flex items-center gap-2">
                Sign in / Create account <ArrowRight size={14} />
              </Link>
            )}
            {user?.role === "member" && !hubMember && (
              <Link to="/member" className="bg-brand-blue text-white px-5 py-3 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2">
                My Portal <ArrowRight size={14} />
              </Link>
            )}
            <a href="#plans" className="border border-white/20 text-white px-5 py-3 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2">
              View Plans
            </a>
          </div>
        </div>
      </section>

      {/* FILTER */}
      <section id="plans" className="py-10 md:py-14 border-b border-white/8">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex flex-wrap gap-2 mb-8" data-testid="membership-filters">
            <FilterBtn active={filter === "all"} onClick={() => setFilter("all")}>All Facilities</FilterBtn>
            {facilities.map((f) => {
              const p = plans.find((x) => x.facility_slug === f);
              return (
                <FilterBtn key={f} active={filter === f} onClick={() => setFilter(f)}>
                  {p?.facility_name || f}
                </FilterBtn>
              );
            })}
          </div>

          {loading ? (
            <div className="text-white/40 py-16 text-center font-mono text-xs uppercase tracking-widest">Loading plans…</div>
          ) : shown.length === 0 ? (
            <div className="text-white/40 py-16 text-center">No plans available.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {shown.map((p) => <PlanCard key={p.id} plan={p} onBuy={handleBuy} isMember={user?.role === "member"} />)}
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-14 md:py-20 bg-[#0F172A] border-b border-white/8">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.4em] brand-orange mb-2">// How it works</div>
          <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tighter mb-10">Simple, transparent, fast</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              { n: "01", t: "Sign up", d: "Create a member account in 30 seconds — email + phone." },
              { n: "02", t: "Buy a plan", d: "Pick hours, overs, sessions or unlimited access for your favourite facility." },
              { n: "03", t: "Reserve slots", d: "Book anytime from the member portal — deducted from your balance." },
              { n: "04", t: "Renew any time", d: "Top-up quota or extend validity from the portal. Never lose a hour." },
            ].map((s, i) => (
              <div key={i} className="border border-white/12 bg-[#131A2B] p-5">
                <div className="font-display text-3xl font-black brand-orange">{s.n}</div>
                <div className="font-display text-lg font-black uppercase mt-2">{s.t}</div>
                <div className="text-sm text-white/60 mt-1">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-white/8 bg-brand-orange text-black">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="font-mono text-xs uppercase tracking-[0.3em] mb-2">// Ready?</div>
            <h2 className="font-display text-3xl md:text-4xl font-black uppercase leading-none">Join today. Play tomorrow.</h2>
          </div>
          <div className="md:justify-self-end flex gap-3">
            {!user ? (
              <>
                <Link to="/member/signup" className="bg-black text-white px-5 py-3 font-bold text-xs uppercase tracking-widest">Sign Up</Link>
                <Link to="/member/login" className="bg-white text-black border border-black px-5 py-3 font-bold text-xs uppercase tracking-widest">Log In</Link>
              </>
            ) : (
              <Link to="/member" className="bg-black text-white px-5 py-3 font-bold text-xs uppercase tracking-widest">My Portal</Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function FilterBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-[11px] font-mono uppercase tracking-widest border transition ${
        active ? "border-brand-orange bg-brand-orange text-black" : "border-white/15 text-white/70 hover:border-white/40"
      }`}
    >
      {children}
    </button>
  );
}

function PlanCard({ plan, onBuy, isMember }) {
  const meta = TYPE_META[plan.plan_type] || TYPE_META.hours;
  const Icon = meta.icon;
  const perUnit = plan.plan_type === "unlimited" ? null : Math.round(plan.price / (plan.quota || 1));
  return (
    <div className="border border-white/12 bg-[#131A2B] hover:border-brand-blue/50 flex flex-col overflow-hidden transition" data-testid={`plan-card-${plan.id}`}>
      <div className="p-5 border-b border-white/10 relative overflow-hidden">
        <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-2">{plan.facility_name}</div>
        <div className="font-display text-xl font-black uppercase leading-tight">{plan.name}</div>
        <div className="mt-4 flex items-baseline gap-2">
          <div className="font-display text-4xl font-black brand-orange">{formatINR(plan.price)}</div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-white/50">
            / {plan.duration_days}d
          </div>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-white/60">
          <Icon size={12} className="brand-blue" />
          {plan.plan_type === "unlimited" ? "Unlimited use" : `${plan.quota} ${meta.unit}`}
          {perUnit && plan.plan_type !== "unlimited" && (
            <span className="text-white/40">· ~₹{perUnit}/{meta.unit.replace(/s$/, "")}</span>
          )}
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        {plan.description && <p className="text-sm text-white/70 mb-3">{plan.description}</p>}
        {plan.guest_limit !== 0 && plan.guest_limit != null && (
          <div className="mb-3 text-[11px] font-mono uppercase tracking-widest brand-blue border border-brand-blue/40 px-2 py-1 self-start">
            + {plan.guest_limit === -1 ? "Unlimited" : plan.guest_limit} guest{plan.guest_limit === 1 ? "" : "s"} per visit
          </div>
        )}
        <ul className="space-y-2 mb-5">
          {(plan.benefits || []).map((b, i) => (
            <li key={i} className="flex gap-2 text-[13px] text-white/80">
              <Check size={14} className="brand-orange shrink-0 mt-0.5" /><span>{b}</span>
            </li>
          ))}
        </ul>
        <button
          onClick={() => onBuy(plan)}
          data-testid={`buy-plan-${plan.id}`}
          className="mt-auto bg-brand-blue text-white py-3 font-bold text-xs uppercase tracking-widest hover:bg-white hover:text-black transition inline-flex items-center gap-2 justify-center"
        >
          {isMember ? "Buy / Renew" : "Sign Up & Buy"} <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
